import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { FormsModule } from '@angular/forms';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';

import {
	AdminItemTypeDto,
	AdminItemTypesListResponse,
	AdminItemTypesService,
} from '../../api/admin-item-types.service';

@Component({
	selector: 'of-admin-item-types-page',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		MatButtonModule,
		MatCardModule,
		MatProgressBarModule,
		MatFormFieldModule,
		MatInputModule,
		MatCheckboxModule,
	],
	template: `
		<div class="page">
			<mat-card class="card" *ngIf="vm$ | async as vm; else loading">
				<h2>Admin · Item Types</h2>
				<p class="muted">Manage item type metadata (global).</p>

				<div class="actions" style="justify-content: flex-end;">
					<button mat-button (click)="refresh()">Refresh</button>
				</div>

				<div style="padding: 12px 0; border-top: 1px solid rgba(255,255,255,0.08);">
					<div style="font-weight: 600; margin-bottom: 8px;">New Item Type</div>
					<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
						<mat-form-field appearance="fill">
							<mat-label>Name</mat-label>
							<input matInput [(ngModel)]="createModel.name" name="createName" />
						</mat-form-field>
						<mat-form-field appearance="fill">
							<mat-label>Category</mat-label>
							<input matInput [(ngModel)]="createModel.category" name="createCategory" />
						</mat-form-field>
						<mat-form-field appearance="fill" style="grid-column: 1 / -1;">
							<mat-label>Description</mat-label>
							<textarea matInput rows="3" [(ngModel)]="createModel.description" name="createDescription"></textarea>
						</mat-form-field>
						<mat-form-field appearance="fill">
							<mat-label>Slot</mat-label>
							<input matInput [(ngModel)]="createModel.slot" name="createSlot" />
						</mat-form-field>
						<mat-form-field appearance="fill">
							<mat-label>Color Token</mat-label>
							<input matInput [(ngModel)]="createModel.colorToken" name="createColorToken" />
						</mat-form-field>
						<mat-form-field appearance="fill" style="grid-column: 1 / -1;">
							<mat-label>Image Asset ID</mat-label>
							<input matInput [(ngModel)]="createModel.imageAssetId" name="createImageAssetId" />
						</mat-form-field>
					</div>

					<div style="display:flex; gap: 12px; align-items:center; margin-top: 8px;">
						<mat-checkbox [(ngModel)]="createModel.isConsumable" name="createIsConsumable">Consumable</mat-checkbox>
						<mat-checkbox [(ngModel)]="createModel.isMagical" name="createIsMagical">Magical</mat-checkbox>
						<span class="spacer"></span>
						<button mat-flat-button color="primary" (click)="create()" [disabled]="creating || !createModel.name">
							Create
						</button>
					</div>
				</div>

				<div
					*ngFor="let t of vm.itemTypes"
					style="padding: 12px 0; border-top: 1px solid rgba(255,255,255,0.08);"
				>
					<div style="display:flex; gap: 12px; align-items: center;">
						<div style="min-width: 0; flex: 1 1 auto;">
							<div style="font-weight: 600;">{{ t.name }}</div>
							<div class="muted" style="font-size: 13px;">
								{{ t.category ?? '—' }} · {{ t.id }}
							</div>
						</div>
						<button mat-button (click)="beginEdit(t)" *ngIf="editingId !== t.id">Edit</button>
						<button mat-button color="warn" (click)="remove(t.id)">Delete</button>
					</div>

					<div *ngIf="editingId === t.id" style="margin-top: 12px;">
						<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
							<mat-form-field appearance="fill">
								<mat-label>Name</mat-label>
								<input matInput [(ngModel)]="editModel.name" name="editName" />
							</mat-form-field>
							<mat-form-field appearance="fill">
								<mat-label>Category</mat-label>
								<input matInput [(ngModel)]="editModel.category" name="editCategory" />
							</mat-form-field>
							<mat-form-field appearance="fill" style="grid-column: 1 / -1;">
								<mat-label>Description</mat-label>
								<textarea matInput rows="3" [(ngModel)]="editModel.description" name="editDescription"></textarea>
							</mat-form-field>
							<mat-form-field appearance="fill">
								<mat-label>Slot</mat-label>
								<input matInput [(ngModel)]="editModel.slot" name="editSlot" />
							</mat-form-field>
							<mat-form-field appearance="fill">
								<mat-label>Color Token</mat-label>
								<input matInput [(ngModel)]="editModel.colorToken" name="editColorToken" />
							</mat-form-field>
							<mat-form-field appearance="fill" style="grid-column: 1 / -1;">
								<mat-label>Image Asset ID</mat-label>
								<input matInput [(ngModel)]="editModel.imageAssetId" name="editImageAssetId" />
							</mat-form-field>
						</div>

						<div style="display:flex; gap: 12px; align-items:center; margin-top: 8px;">
							<mat-checkbox [(ngModel)]="editModel.isConsumable" name="editIsConsumable">Consumable</mat-checkbox>
							<mat-checkbox [(ngModel)]="editModel.isMagical" name="editIsMagical">Magical</mat-checkbox>
							<span class="spacer"></span>
							<button mat-button (click)="cancelEdit()">Cancel</button>
							<button mat-flat-button color="primary" (click)="save()" [disabled]="saving || !editModel.name">
								Save
							</button>
						</div>
					</div>
				</div>
			</mat-card>
		</div>

		<ng-template #loading>
			<div class="page">
				<mat-card class="card">
					<mat-progress-bar mode="indeterminate"></mat-progress-bar>
				</mat-card>
			</div>
		</ng-template>
	`,
})
export class AdminItemTypesPage {
	private readonly refresh$ = new BehaviorSubject<void>(undefined);
	vm$: Observable<AdminItemTypesListResponse>;

	creating = false;
	saving = false;

	editingId: string | null = null;
	editModel: Partial<AdminItemTypeDto> = {};

	createModel: {
		name: string;
		description: string;
		category: string;
		isConsumable: boolean;
		isMagical: boolean;
		slot: string;
		colorToken: string;
		imageAssetId: string;
	} = {
		name: '',
		description: '',
		category: '',
		isConsumable: false,
		isMagical: false,
		slot: '',
		colorToken: '',
		imageAssetId: '',
	};

	constructor(private readonly admin: AdminItemTypesService) {
		this.vm$ = this.refresh$.pipe(switchMap(() => this.admin.list()));
	}

	refresh() {
		this.refresh$.next();
	}

	beginEdit(row: AdminItemTypeDto) {
		this.editingId = row.id;
		this.editModel = { ...row };
	}

	cancelEdit() {
		this.editingId = null;
		this.editModel = {};
	}

	create() {
		if (this.creating) return;
		if (!this.createModel.name.trim()) return;
		this.creating = true;
		this.admin
			.create({
				name: this.createModel.name.trim(),
				description: this.createModel.description || null,
				category: this.createModel.category || null,
				isConsumable: this.createModel.isConsumable,
				isMagical: this.createModel.isMagical,
				slot: this.createModel.slot || null,
				colorToken: this.createModel.colorToken || null,
				imageAssetId: this.createModel.imageAssetId || null,
			})
			.subscribe({
				next: () => {
					this.creating = false;
					this.createModel = {
						name: '',
						description: '',
						category: '',
						isConsumable: false,
						isMagical: false,
						slot: '',
						colorToken: '',
						imageAssetId: '',
					};
					this.refresh();
				},
				error: () => {
					this.creating = false;
					this.refresh();
				},
			});
	}

	save() {
		if (!this.editingId || this.saving) return;
		if (!this.editModel.name?.trim()) return;
		this.saving = true;
		this.admin
			.update(this.editingId, {
				name: this.editModel.name.trim(),
				description: this.editModel.description ?? null,
				category: this.editModel.category ?? null,
				isConsumable: !!this.editModel.isConsumable,
				isMagical: !!this.editModel.isMagical,
				slot: this.editModel.slot ?? null,
				colorToken: this.editModel.colorToken ?? null,
				imageAssetId: this.editModel.imageAssetId ?? null,
			})
			.subscribe({
				next: () => {
					this.saving = false;
					this.cancelEdit();
					this.refresh();
				},
				error: () => {
					this.saving = false;
					this.refresh();
				},
			});
	}

	remove(id: string) {
		this.admin.delete(id).subscribe({
			next: () => {
				if (this.editingId === id) this.cancelEdit();
				this.refresh();
			},
			error: () => this.refresh(),
		});
	}
}
